import { useEffect, useState, useMemo } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getPubInfo, GetPubInfoOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  MapPin, Phone, Mail, Globe, Clock, Search, ChevronDown, ChevronUp,
  Building2, Users, Table2, Info, ExternalLink, Maximize2,
} from 'lucide-react';

type PubData = GetPubInfoOutputType;
type Room = PubData['rooms'][0];
type QAItem = PubData['qaItems'][0];

// ── Opening Hours parser ──────────────────────────────────────────────────────
function OpeningHoursDisplay({ text }: { text: string }) {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const today = days[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
  const lines = text.trim().split('\n').filter(Boolean);
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        const isToday = days.some(d => line.toLowerCase().includes(d.toLowerCase()) && d === today);
        return (
          <div key={i} className={`flex items-center justify-between py-1.5 px-3 rounded-lg text-sm ${isToday ? 'bg-primary/10 border border-primary/30' : 'bg-muted/40'}`}>
            <span className={`font-medium ${isToday ? 'text-primary' : 'text-foreground'}`}>
              {isToday && <span className="text-xs mr-1.5 bg-primary text-primary-foreground rounded px-1.5 py-0.5">Today</span>}
              {line.split(':')[0]}
            </span>
            <span className={isToday ? 'text-primary font-semibold' : 'text-muted-foreground'}>
              {line.split(':').slice(1).join(':').trim()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Q&A Accordion ─────────────────────────────────────────────────────────────
function QAAccordion({ items }: { items: QAItem[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="space-y-2">
      {items.map(item => (
        <div key={item.id} className="border border-border rounded-lg overflow-hidden">
          <button
            className="w-full flex items-start justify-between gap-3 p-3.5 text-left hover:bg-muted/30 transition-colors"
            onClick={() => setOpen(open === item.id ? null : item.id)}
          >
            <span className="font-medium text-foreground text-sm">{item.question}</span>
            {open === item.id
              ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
          </button>
          {open === item.id && (
            <div className="px-3.5 pb-3.5 pt-0 text-sm text-muted-foreground leading-relaxed border-t border-border bg-muted/20 whitespace-pre-wrap">
              {item.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Room / Floor Plan card ────────────────────────────────────────────────────
function RoomCard({ room }: { room: Room }) {
  const [imgExpanded, setImgExpanded] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      {room.floorPlanUrl && (
        <div className="relative group cursor-pointer" onClick={() => setImgExpanded(!imgExpanded)}>
          <img
            src={room.floorPlanUrl}
            alt={`${room.name} floor plan`}
            className={`w-full object-cover transition-all ${imgExpanded ? 'max-h-[600px] object-contain bg-muted/30' : 'max-h-56 object-cover'}`}
          />
          <div className="absolute top-2 right-2 bg-background/80 backdrop-blur rounded-md p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Maximize2 className="w-4 h-4 text-foreground" />
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-foreground">{room.name}</h3>
          <div className="flex items-center gap-2">
            {room.tables.length > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Table2 className="w-3.5 h-3.5" />{room.tables.length} tables
              </span>
            )}
            {room.totalCapacity > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />up to {room.totalCapacity}
              </span>
            )}
          </div>
        </div>
        {room.description && <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{room.description}</p>}
        {(room.widthMetres || room.lengthMetres) && (
          <p className="text-xs text-muted-foreground mb-3">
            Dimensions: {room.widthMetres}m × {room.lengthMetres}m
            {room.heightMetres ? ` · ${room.heightMetres}m ceiling` : ''}
          </p>
        )}
        {room.tables.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Tables</p>
            <div className="flex flex-wrap gap-1.5">
              {room.tables.map(t => (
                <div key={t.id} className="bg-muted/60 border border-border rounded-lg px-2.5 py-1.5 text-center min-w-[64px]">
                  <p className="text-xs font-bold text-foreground">T{t.tableNumber}</p>
                  <p className="text-[10px] text-muted-foreground">{t.minCapacity && t.maxCapacity ? `${t.minCapacity}–${t.maxCapacity}` : t.maxCapacity ? `up to ${t.maxCapacity}` : '?'} ppl</p>
                  <p className="text-[10px] text-muted-foreground capitalize">{t.shape}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PubInfo() {
  const { user } = useAuth();
  const [data, setData] = useState<PubData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    if (!user) return;
    getPubInfo({}).then(setData).finally(() => setLoading(false));
  }, [user]);

  const categories = useMemo(() => {
    if (!data) return [];
    const cats = [...new Set<string>(data.qaItems.map((q: QAItem) => String(q.category)))].sort();
    return ['All', ...cats];
  }, [data]);

  const filteredQA = useMemo(() => {
    if (!data) return [];
    return data.qaItems.filter(q => {
      const matchesCat = activeCategory === 'All' || q.category === activeCategory;
      const matchesSearch = !search || q.question.toLowerCase().includes(search.toLowerCase()) || q.answer.toLowerCase().includes(search.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [data, search, activeCategory]);

  const groupedQA = useMemo(() => {
    const groups: Record<string, QAItem[]> = {};
    filteredQA.forEach(q => {
      if (!groups[q.category]) groups[q.category] = [];
      groups[q.category].push(q);
    });
    return groups;
  }, [filteredQA]);

  const info = data?.info;
  const pubName = info?.pubName || 'The Rattlebone';

  if (loading) return (
    <div className="px-6 py-8 max-w-4xl mx-auto space-y-4">
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-12 w-64" />
      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero header */}
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-6 max-w-5xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
              <Building2 className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground">{pubName}</h1>
              {info && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {[info.addressLine1, info.town, info.postcode].filter(Boolean).join(', ')}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {info?.phone && (
                  <a href={`tel:${info.phone}`} className="flex items-center gap-1.5 text-xs text-primary hover:underline">
                    <Phone className="w-3.5 h-3.5" />{info.phone}
                  </a>
                )}
                {info?.email && (
                  <a href={`mailto:${info.email}`} className="flex items-center gap-1.5 text-xs text-primary hover:underline">
                    <Mail className="w-3.5 h-3.5" />{info.email}
                  </a>
                )}
                {info?.website && (
                  <a href={info.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-primary hover:underline">
                    <Globe className="w-3.5 h-3.5" />Website <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-5xl mx-auto">
        <Tabs defaultValue="info">
          <TabsList className="mb-6">
            <TabsTrigger value="info"><Info className="w-3.5 h-3.5 mr-1.5" />Info</TabsTrigger>
            <TabsTrigger value="qa">
              Q&amp;A
              {data?.qaItems.length ? <span className="ml-1.5 text-xs opacity-70">({data.qaItems.length})</span> : null}
            </TabsTrigger>
            <TabsTrigger value="rooms">
              <Building2 className="w-3.5 h-3.5 mr-1.5" />Rooms &amp; Floor Plans
              {data?.rooms.length ? <span className="ml-1.5 text-xs opacity-70">({data.rooms.length})</span> : null}
            </TabsTrigger>
          </TabsList>

          {/* INFO TAB */}
          <TabsContent value="info">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-5">
                {/* Address */}
                {info && (info.addressLine1 || info.town) && (
                  <div className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-primary" />
                      <p className="font-semibold text-foreground text-sm">Address</p>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-0.5">
                      {info.addressLine1 && <p>{info.addressLine1}</p>}
                      {info.addressLine2 && <p>{info.addressLine2}</p>}
                      {info.town && <p>{info.town}{info.county ? `, ${info.county}` : ''}</p>}
                      {info.postcode && <p className="font-medium text-foreground">{info.postcode}</p>}
                    </div>
                    {info.addressLine1 && (
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent([info.addressLine1, info.town, info.postcode].filter(Boolean).join(', '))}`}
                        target="_blank" rel="noopener noreferrer"
                        className="mt-3 flex items-center gap-1.5 text-xs text-primary hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" /> Open in Google Maps
                      </a>
                    )}
                  </div>
                )}
                {/* Contact */}
                {info && (info.phone || info.email || info.website) && (
                  <div className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Phone className="w-4 h-4 text-primary" />
                      <p className="font-semibold text-foreground text-sm">Contact</p>
                    </div>
                    <div className="space-y-2">
                      {info.phone && <a href={`tel:${info.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"><Phone className="w-3.5 h-3.5 text-primary" />{info.phone}</a>}
                      {info.email && <a href={`mailto:${info.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"><Mail className="w-3.5 h-3.5 text-primary" />{info.email}</a>}
                      {info.website && <a href={info.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"><Globe className="w-3.5 h-3.5 text-primary" />{info.website}</a>}
                    </div>
                  </div>
                )}
                {/* Emergency */}
                {info?.emergencyContact && (
                  <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-5">
                    <p className="text-xs font-bold text-destructive uppercase tracking-wider mb-2">Emergency Contact</p>
                    <p className="text-sm text-foreground">{info.emergencyContact}</p>
                  </div>
                )}
              </div>

              <div className="space-y-5">
                {/* Opening Hours */}
                {info?.openingHours && (
                  <div className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-primary" />
                      <p className="font-semibold text-foreground text-sm">Opening Hours</p>
                    </div>
                    <OpeningHoursDisplay text={info.openingHours} />
                  </div>
                )}
                {/* About */}
                {info?.about && (
                  <div className="bg-card border border-border rounded-xl p-5">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">About The Pub</p>
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{info.about}</p>
                  </div>
                )}
              </div>
            </div>

            {!info && (
              <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-xl">
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Pub information hasn't been set up yet. Ask an admin to add it.</p>
              </div>
            )}
          </TabsContent>

          {/* Q&A TAB */}
          <TabsContent value="qa">
            {(data?.qaItems.length ?? 0) === 0 ? (
              <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-xl">
                <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No Q&amp;A items added yet.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questions..." className="pl-9" />
                  </div>
                </div>
                {/* Category filter */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {categories.map(cat => (
                    <button key={cat} onClick={() => setActiveCategory(cat)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${activeCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                      {cat}
                    </button>
                  ))}
                </div>

                {Object.keys(groupedQA).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No results found for "{search}"</p>
                ) : activeCategory === 'All' ? (
                  Object.entries(groupedQA).map(([cat, items]) => (
                    <div key={cat} className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className="text-xs">{cat}</Badge>
                        <span className="text-xs text-muted-foreground">{items.length} items</span>
                      </div>
                      <QAAccordion items={items} />
                    </div>
                  ))
                ) : (
                  <QAAccordion items={filteredQA} />
                )}
              </>
            )}
          </TabsContent>

          {/* ROOMS TAB */}
          <TabsContent value="rooms">
            {(data?.rooms.length ?? 0) === 0 ? (
              <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-xl">
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No rooms or floor plans added yet.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {data!.rooms.map(room => <RoomCard key={room.id} room={room} />)}
                </div>
                <div className="mt-4 p-3 bg-muted/30 border border-border rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">Total venue capacity: </span>
                    {data!.rooms.reduce((sum, r) => sum + r.totalCapacity, 0)} people across {data!.rooms.length} spaces
                  </p>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
